import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Collapse from '@material-ui/core/Collapse';
import Chip from '@material-ui/core/Chip';
import IconButton from '@material-ui/core/IconButton';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import Skeleton from '@material-ui/lab/Skeleton';
import { Button, Card, Col, Row, Select, Typography, ViewContainer } from 'components/AORedesign';
import { Feature } from 'components/Feature';
import featureFlags from 'constants/featureFlags';
import moment from 'moment';
import { setAppSnackbarProps } from 'redux/app-store';
import { getHealthCheckRunDetail, getHealthCheckRunsList, initiateHealthCheck } from 'services/integration/aoc-api-health-check-service';

const useStyles = makeStyles((theme) => ({
  suiteCard: {
    marginBottom: theme.spacing(2),
  },
  suiteHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1.5, 2),
    borderRadius: 4,
  },
  suiteHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  regionSection: {
    marginTop: theme.spacing(2),
  },
  compactTable: {
    '& .MuiTableCell-root': {
      padding: theme.spacing(0.75, 1),
      fontSize: '0.875rem',
    },
  },
  failedRow: {
    backgroundColor: theme.palette.alert.errorFill,
  },
  detailList: {
    paddingLeft: theme.spacing(3),
    margin: theme.spacing(1, 0),
  },
  detailItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  detailIcon: {
    marginTop: 2,
    fontSize: '1rem',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  passedText: {
    color: theme.palette.success.main,
    fontWeight: 500,
  },
  failedText: {
    color: theme.palette.error.main,
    fontWeight: 500,
  },
  passedChip: {
    backgroundColor: theme.palette.alert.successFill,
    color: theme.palette.success.main,
  },
  failedChip: {
    backgroundColor: theme.palette.alert.errorFill,
    color: theme.palette.error.main,
  },
  summaryBox: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 4,
  },
  suiteName: {
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },
  regionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  expandCell: {
    width: 40,
    padding: theme.spacing(0.5),
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
}));

/**
 * Determines if a service row has any failures across its subnet columns.
 */
const serviceHasFailure = (row) => {
  return row.columns.some((col) => col.status.toLowerCase() === 'failed');
};

const HealthChecks = () => {
  const classes = useStyles();
  const [runIds, setRunIds] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [runDetails, setRunDetails] = useState(null);
  const [expandedSuites, setExpandedSuites] = useState({});
  const [expandedDetails, setExpandedDetails] = useState({});
  const dispatch = useDispatch();

  const timezoneOffset = moment().utcOffset();

  const dispatchSnackBarMessage = useCallback(
    (severity, message) => {
      dispatch(
        setAppSnackbarProps({
          message: message,
          severity: severity,
        }),
      );
    },
    [dispatch],
  );

  const fetchRunIds = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getHealthCheckRunsList();
      setRunIds(data);
      setSelectedRunId('');
    } catch (error) {
      console.error(error);
      dispatchSnackBarMessage('error', "Error retrieving list of Run ID's. See console log for details.");
    } finally {
      setIsLoading(false);
    }
  }, [dispatchSnackBarMessage]);

  const fetchRunDetails = useCallback(
    async (runId) => {
      try {
        setIsLoading(true);
        const details = await getHealthCheckRunDetail(runId);
        setRunDetails(details);
      } catch (error) {
        console.error(error);
        dispatchSnackBarMessage('error', 'Error retrieving details for run id: ' + runId + '. See console log for details.');
      } finally {
        setIsLoading(false);
      }
    },
    [dispatchSnackBarMessage],
  );

  useEffect(() => {
    fetchRunIds();
  }, [fetchRunIds]);

  useEffect(() => {
    const selectBoxChangeUseEffect = async () => {
      if (!selectedRunId && runIds.length > 0) {
        setSelectedRunId(runIds[0]);
      } else if (selectedRunId) {
        await fetchRunDetails(selectedRunId);
      }
    };
    selectBoxChangeUseEffect();
  }, [selectedRunId, runIds, fetchRunDetails]);

  /**
   * All sections default to collapsed.
   */
  useEffect(() => {
    if (!runDetails) return;
    setExpandedSuites({});
    setExpandedDetails({});
  }, [runDetails]);

  const handleRefreshButtonClick = async () => {
    await fetchRunIds();
  };

  const handleNewHealthCheckButtonClick = async () => {
    try {
      const data = await initiateHealthCheck();
      if (data.success) {
        const localRunId = moment.utc(data.run_id, 'YYYYMMDDHHmmss').utcOffset(timezoneOffset).format('YYYY-MM-DD HH:mm:ss');
        dispatchSnackBarMessage('success', data.message + ' Run ID: ' + localRunId);
      } else {
        dispatchSnackBarMessage('error', data.error);
      }
    } catch (error) {
      console.error(error);
      dispatchSnackBarMessage('error', 'Error instantiating a new Health Check Run. See console log for details.');
    }
  };

  const handleRunIdChange = (event) => {
    setSelectedRunId(event.target.value);
  };

  const toggleSuite = (suiteIdx) => {
    setExpandedSuites((prev) => ({ ...prev, [suiteIdx]: !prev[suiteIdx] }));
  };

  const toggleDetail = (key) => {
    setExpandedDetails((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getStatusClass = (status) => {
    return status.toLowerCase() === 'passed' ? classes.passedText : classes.failedText;
  };

  /**
   * Counts services passed/failed for a given region across all suites.
   */
  const countServiceResults = (regionIdx) => {
    let suitesPassed = 0;
    let suitesFailed = 0;
    let servicesPassed = 0;
    let servicesFailed = 0;
    let totalServices = 0;

    runDetails.rows.forEach((row) => {
      const col = row.columns[regionIdx];
      if (!col) return;

      if (col.status.toLowerCase() === 'passed') {
        suitesPassed++;
      } else {
        suitesFailed++;
      }

      if (col.details && col.details.rows) {
        col.details.rows.forEach((serviceRow) => {
          totalServices++;
          const hasFail = serviceRow.columns.some((serviceCol) => serviceCol.status && serviceCol.status.toLowerCase() === 'failed');
          if (hasFail) {
            servicesFailed++;
          } else {
            servicesPassed++;
          }
        });
      }
    });

    return { suitesPassed, suitesFailed, servicesPassed, servicesFailed, totalServices };
  };

  /**
   * Renders summary chips showing suite-level and service-level pass/fail counts per region.
   */
  const renderSummaryChips = () => {
    if (!runDetails || !runDetails.headers || runDetails.headers.length < 2) return null;

    const regionHeaders = runDetails.headers.slice(1);
    const totalSuites = runDetails.rows.length;

    return (
      <Box className={classes.summaryBox}>
        <Typography variant="h6" style={{ fontWeight: 600, marginRight: 8 }}>
          Summary
        </Typography>
        {regionHeaders.map((region, regionIdx) => {
          const counts = countServiceResults(regionIdx);
          return (
            <React.Fragment key={region}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                {region}:
              </Typography>
              <Chip
                size="medium"
                label={`Suites: ${counts.suitesPassed}/${totalSuites} passed`}
                className={counts.suitesFailed === 0 ? classes.passedChip : classes.failedChip}
                icon={counts.suitesFailed === 0
                  ? <CheckCircleOutlineIcon style={{ color: 'inherit' }} />
                  : <ErrorOutlineIcon style={{ color: 'inherit' }} />}
              />
              <Chip
                size="medium"
                label={`Services: ${counts.servicesPassed}/${counts.totalServices} passed`}
                className={counts.servicesFailed === 0 ? classes.passedChip : classes.failedChip}
                icon={counts.servicesFailed === 0
                  ? <CheckCircleOutlineIcon style={{ color: 'inherit' }} />
                  : <ErrorOutlineIcon style={{ color: 'inherit' }} />}
              />
              {counts.servicesFailed > 0 && (
                <Chip
                  size="medium"
                  label={`${counts.servicesFailed} service${counts.servicesFailed !== 1 ? 's' : ''} failed`}
                  className={classes.failedChip}
                  icon={<ErrorOutlineIcon style={{ color: 'inherit' }} />}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  /**
   * Renders the status chips for a suite header showing per-region service counts.
   */
  const renderSuiteStatusChips = (row) => {
    const regionHeaders = runDetails.headers.slice(1);
    return regionHeaders.map((region, idx) => {
      const col = row.columns[idx];
      if (!col) return null;
      const isPassed = col.status.toLowerCase() === 'passed';

      let totalServices = 0;
      let servicesPassed = 0;
      if (col.details && col.details.rows) {
        col.details.rows.forEach((serviceRow) => {
          totalServices++;
          const hasFail = serviceRow.columns.some((c) => c.status && c.status.toLowerCase() === 'failed');
          if (!hasFail) servicesPassed++;
        });
      }

      const label = totalServices > 0
        ? `${region}: ${servicesPassed}/${totalServices} services passed`
        : `${region}: ${col.status}`;

      return (
        <Chip
          key={region}
          size="small"
          label={label}
          className={isPassed ? classes.passedChip : classes.failedChip}
        />
      );
    });
  };

  /**
   * Renders the inline detail rows for a single service within a region's subnet table.
   * subnetHeaders maps each column index to its subnet name.
   */
  const renderServiceDetails = (suiteIdx, regionIdx, serviceIdx, serviceRow, subnetHeaders) => {
    const key = `${suiteIdx}-${regionIdx}-${serviceIdx}`;
    const isExpanded = expandedDetails[key] || false;

    const allDetails = [];
    serviceRow.columns.forEach((col, colIdx) => {
      if (col.details && Array.isArray(col.details)) {
        const subnet = subnetHeaders[colIdx] || '';
        col.details.forEach((detail) => {
          allDetails.push({ ...detail, subnet });
        });
      }
    });

    if (allDetails.length === 0) return null;

    return (
      <TableRow>
        <TableCell colSpan={serviceRow.columns.length + 2} style={{ padding: 0, borderBottom: isExpanded ? undefined : 'none' }}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box className={classes.detailList}>
              {allDetails.map((detail, detailIdx) => {
                const isPassed = detail.status && detail.status.toLowerCase() === 'passed';
                return (
                  <div key={detailIdx} className={classes.detailItem}>
                    {isPassed ? (
                      <CheckCircleOutlineIcon className={classes.detailIcon} style={{ color: '#367c3a' }} />
                    ) : (
                      <ErrorOutlineIcon className={classes.detailIcon} style={{ color: '#da190b' }} />
                    )}
                    <Typography variant="body2">
                      {detail.subnet && <span style={{ color: '#757575' }}>[{detail.subnet}] </span>}
                      <strong>{detail.testName}</strong>
                      {detail.message ? ` - ${detail.message}` : ''}
                    </Typography>
                  </div>
                );
              })}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  /**
   * Renders the subnet table for a single region within a suite.
   */
  const renderRegionTable = (suiteIdx, regionIdx, regionDetails) => {
    if (!regionDetails || !regionDetails.rows || regionDetails.rows.length === 0) return null;

    // Subnet headers are all headers after the first (service name) column
    const subnetHeaders = regionDetails.headers.slice(1);

    return (
      <div className={classes.tableWrapper}>
        <Table size="small" className={classes.compactTable}>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 40 }} />
              {regionDetails.headers.map((header, idx) => (
                <TableCell key={idx} style={{ fontWeight: 'bold' }}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {regionDetails.rows.map((serviceRow, serviceIdx) => {
              const key = `${suiteIdx}-${regionIdx}-${serviceIdx}`;
              const isExpanded = expandedDetails[key] || false;
              const hasFail = serviceHasFailure(serviceRow);

              const hasDetails = serviceRow.columns.some((col) => col.details && Array.isArray(col.details) && col.details.length > 0);

              return (
                <React.Fragment key={serviceIdx}>
                  <TableRow className={hasFail ? classes.failedRow : ''}>
                    <TableCell className={classes.expandCell}>
                      {hasDetails && (
                        <IconButton size="small" onClick={() => toggleDetail(key)} aria-label="expand row">
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>{serviceRow.name}</TableCell>
                    {serviceRow.columns.map((col, colIdx) => (
                      <TableCell key={colIdx} className={getStatusClass(col.status)}>
                        {col.status}
                      </TableCell>
                    ))}
                  </TableRow>
                  {hasDetails && renderServiceDetails(suiteIdx, regionIdx, serviceIdx, serviceRow, subnetHeaders)}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  /**
   * Renders a single suite card with inline region tables.
   */
  const renderSuiteCard = (row, suiteIdx) => {
    const isExpanded = expandedSuites[suiteIdx] || false;
    const regionHeaders = runDetails.headers.slice(1);

    return (
      <Card key={suiteIdx} className={classes.suiteCard}>
        <div
          className={classes.suiteHeader}
          role="button"
          tabIndex={0}
          onClick={() => toggleSuite(suiteIdx)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleSuite(suiteIdx);
            }
          }}
        >
          <div className={classes.suiteHeaderLeft}>
            <Typography variant="subtitle1" className={classes.suiteName}>
              {row.name}
            </Typography>
            {renderSuiteStatusChips(row)}
          </div>
          <IconButton size="small" aria-label="toggle suite">
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </div>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box mt={1}>
            {regionHeaders.map((region, regionIdx) => {
              const col = row.columns[regionIdx];
              if (!col) return null;
              const isPassed = col.status.toLowerCase() === 'passed';
              return (
                <div key={region} className={classes.regionSection}>
                  <Typography variant="body1" className={classes.regionTitle}>
                    {region}:{' '}
                    <span className={isPassed ? classes.passedText : classes.failedText}>{col.status}</span>
                  </Typography>
                  {renderRegionTable(suiteIdx, regionIdx, col.details)}
                </div>
              );
            })}
          </Box>
        </Collapse>
      </Card>
    );
  };

  /**
   * Renders the full results section with summary chips and suite cards.
   */
  const renderResults = () => {
    if (isLoading) {
      return (
        <Box mt={2}>
          <Skeleton variant="rect" height={60} width="100%" style={{ marginBottom: 16 }} />
          <Skeleton variant="rect" height={200} width="100%" style={{ marginBottom: 16 }} />
          <Skeleton variant="rect" height={200} width="100%" />
        </Box>
      );
    }

    if (!runDetails) return null;

    const localRunId = moment.utc(selectedRunId, 'YYYYMMDDHHmmss').utcOffset(timezoneOffset).format('YYYY-MM-DD HH:mm:ss');

    return (
      <Box mt={2}>
        <Typography variant="h6" gutterBottom>
          Health Check Summary for {localRunId}
        </Typography>

        {renderSummaryChips()}

        {runDetails.rows.map((row, suiteIdx) => renderSuiteCard(row, suiteIdx))}
      </Box>
    );
  };

  return (
    <Feature allowed={featureFlags.ACCESS_HEALTH_CHECKS}>
      <ViewContainer title="Health Checks" hidePolicyViewChange={true} hideStartQuote={true}>
        <Card>
          <Typography variant="h6" gutterBottom>
            Select Run ID
          </Typography>
          <Row alignItems="center">
            <Col>
              {runIds.length > 0 ? (
                <Select
                  labelId="runIds-label"
                  disabled={isLoading}
                  value={selectedRunId}
                  onChange={handleRunIdChange}
                  menuItems={runIds.map((runId) => ({
                    value: runId,
                    label: moment.utc(runId, 'YYYYMMDDHHmmss').utcOffset(timezoneOffset).format('YYYY-MM-DD HH:mm:ss'),
                  }))}
                  style={{ marginRight: '10px' }}
                />
              ) : (
                <span>No run IDs available</span>
              )}
            </Col>
            <Col>
              <div className={classes.controlsRow}>
                <Button color="primary" id="runids-refresh-button" variant="contained" disabled={isLoading} onClick={handleRefreshButtonClick}>
                  Refresh
                </Button>
                <Button color="primary" id="initiate-health-check" variant="contained" onClick={handleNewHealthCheckButtonClick}>
                  Initiate new Health Check Run
                </Button>
              </div>
            </Col>
          </Row>
        </Card>

        {renderResults()}
      </ViewContainer>
    </Feature>
  );
};

export default HealthChecks;
